import React, { useRef, useState } from 'react';
import { Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useChipFit } from '@/hooks/useChipFit';
import { FEATURE_FLAG_TARGETS } from '../targets';
import { FeatureFlagTargetChips } from '../FeatureFlagTargetChips';
import { useFeatureFlagRules } from '../useFeatureFlagRules';

// One line of small chips plus the cell's vertical padding
const COLLAPSED_HEIGHT = 40;

/**
 * A feature flag's users, broker groups or ledgers (`field`) as chips. Collapsed, it shows
 * one line: the chips that fit (useChipFit, as OverflowChips does) and "+x" for the rest,
 * so the row's height comes from the other cells. The arrow button expands it to every chip,
 * wrapped, and collapses it again, animated with Collapse. Clicking anywhere else in the
 * cell opens the edit dialog (addFeatureFlagTargets); a chip's × removes it. An empty cell
 * shows "Add <noun>" on hover. Fields the rules don't allow editing (adminModeEnabled's
 * broker groups and ledgers) don't open the dialog.
 */
export const FeatureFlagTargetsCell = ({ flag, field }) => {
	const dispatch = useDispatch();
	const { noun } = FEATURE_FLAG_TARGETS[field];
	const { canEdit } = useFeatureFlagRules(flag, field);
	const chipsRef = useRef();
	const [expanded, setExpanded] = useState(false);
	// Every chip is shown from the start of expanding until collapsing has finished, so the
	// chips stay in place while the height animates
	const [showAll, setShowAll] = useState(false);

	const count = flag[field]?.length ?? 0;
	const fitCount = useChipFit(chipsRef, count);
	const hiddenCount = showAll ? 0 : Math.max(count - fitCount, 0);

	const handleOpen = () => {
		if (!canEdit) return;
		dispatch(openDialog({ dialogName: 'addFeatureFlagTargets', data: { flagId: flag.id, field } }));
	};

	const handleToggle = (event) => {
		// Expanding or collapsing shouldn't also open the dialog
		event.stopPropagation();
		if (!expanded) {
			setShowAll(true);
		}
		setExpanded(!expanded);
	};

	return (
		<Stack
			direction='row'
			alignItems='center'
			spacing={0.5}
			onClick={handleOpen}
			sx={{
				width: '100%',
				height: '100%',
				cursor: canEdit ? 'pointer' : 'default',
				'&:hover .feature-flag-targets-hint': { visibility: 'visible' },
			}}
		>
			<Collapse
				in={expanded}
				collapsedSize={COLLAPSED_HEIGHT}
				onExited={() => setShowAll(false)}
				sx={{ flex: 1, minWidth: 0 }}
			>
				<Stack
					ref={chipsRef}
					direction='row'
					flexWrap={showAll ? 'wrap' : 'nowrap'}
					gap={0.5}
					alignItems='center'
					sx={{ minWidth: 0, minHeight: COLLAPSED_HEIGHT, overflow: 'hidden', py: 1 }}
				>
					{count ? (
						<FeatureFlagTargetChips
							flagId={flag.id}
							field={field}
							{...(!showAll && { limit: fitCount })}
						/>
					) : (
						<Typography
							className='feature-flag-targets-hint'
							variant='body2'
							color='text.secondary'
							sx={{ visibility: 'hidden' }}
						>
							{canEdit ? `Add ${noun}` : 'Admin mode is for individual users only'}
						</Typography>
					)}
					{hiddenCount > 0 && (
						<Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
							{`+${hiddenCount}`}
						</Typography>
					)}
				</Stack>
			</Collapse>
			{(showAll || hiddenCount > 0) && (
				<Tooltip title={expanded ? 'Show less' : `Show all ${noun}`}>
					<IconButton
						size='small'
						onClick={handleToggle}
						aria-label={expanded ? `Show fewer ${noun}` : `Show all ${noun}`}
						aria-expanded={expanded}
					>
						{expanded ? (
							<KeyboardArrowUpIcon fontSize='small' />
						) : (
							<KeyboardArrowDownIcon fontSize='small' />
						)}
					</IconButton>
				</Tooltip>
			)}
		</Stack>
	);
};
